/**
 * Phone number utility functions for normalization, validation, and formatting.
 * Handles Arabic numerals (٠-٩) and various phone formats.
 * Supports E.164 international format for canonical storage.
 */

/** Map of Arabic-Indic numerals to Western Arabic numerals */
const ARABIC_TO_ENGLISH: Record<string, string> = {
  '٠': '0',
  '١': '1',
  '٢': '2',
  '٣': '3',
  '٤': '4',
  '٥': '5',
  '٦': '6',
  '٧': '7',
  '٨': '8',
  '٩': '9',
};

/**
 * Country phone configuration for E.164 normalization.
 * Each country has a dial code and rules for normalizing local numbers.
 */
export interface CountryPhoneConfig {
  /** ISO 3166-1 alpha-2 country code */
  code: string;
  /** Country name for display */
  name: string;
  /** International dial code (e.g., '971' for UAE) */
  dialCode: string;
  /** Flag emoji */
  flag: string;
  /** Number of digits in a local phone number (after removing leading 0) */
  localDigits: number;
  /** Whether local numbers commonly start with 0 */
  hasLeadingZero: boolean;
  /** Example local number for placeholder */
  example: string;
}

/** Supported countries with phone configuration */
export const COUNTRY_PHONE_CONFIGS: CountryPhoneConfig[] = [
  {
    code: 'AE',
    name: 'United Arab Emirates',
    dialCode: '971',
    flag: '🇦🇪',
    localDigits: 9, // e.g., 501234567 (after removing leading 0)
    hasLeadingZero: true,
    example: '50 123 4567', // Shows format without 0 - both 501234567 and 0501234567 are valid
  },
  {
    code: 'SA',
    name: 'Saudi Arabia',
    dialCode: '966',
    flag: '🇸🇦',
    localDigits: 9,
    hasLeadingZero: true,
    example: '50 123 4567', // Both 501234567 and 0501234567 are valid
  },
  {
    code: 'KW',
    name: 'Kuwait',
    dialCode: '965',
    flag: '🇰🇼',
    localDigits: 8,
    hasLeadingZero: false,
    example: '5012 3456',
  },
  {
    code: 'BH',
    name: 'Bahrain',
    dialCode: '973',
    flag: '🇧🇭',
    localDigits: 8,
    hasLeadingZero: false,
    example: '3312 3456',
  },
  {
    code: 'QA',
    name: 'Qatar',
    dialCode: '974',
    flag: '🇶🇦',
    localDigits: 8,
    hasLeadingZero: false,
    example: '5012 3456',
  },
  {
    code: 'OM',
    name: 'Oman',
    dialCode: '968',
    flag: '🇴🇲',
    localDigits: 8,
    hasLeadingZero: false,
    example: '9212 3456',
  },
  {
    code: 'EG',
    name: 'Egypt',
    dialCode: '20',
    flag: '🇪🇬',
    localDigits: 10,
    hasLeadingZero: true,
    example: '0100 123 4567',
  },
  {
    code: 'JO',
    name: 'Jordan',
    dialCode: '962',
    flag: '🇯🇴',
    localDigits: 9,
    hasLeadingZero: true,
    example: '079 123 4567',
  },
  {
    code: 'LB',
    name: 'Lebanon',
    dialCode: '961',
    flag: '🇱🇧',
    localDigits: 8,
    hasLeadingZero: true,
    example: '03 123 456',
  },
  {
    code: 'PK',
    name: 'Pakistan',
    dialCode: '92',
    flag: '🇵🇰',
    localDigits: 10,
    hasLeadingZero: true,
    example: '0300 1234567',
  },
  {
    code: 'IN',
    name: 'India',
    dialCode: '91',
    flag: '🇮🇳',
    localDigits: 10,
    hasLeadingZero: false,
    example: '98765 43210',
  },
  {
    code: 'PH',
    name: 'Philippines',
    dialCode: '63',
    flag: '🇵🇭',
    localDigits: 10,
    hasLeadingZero: true,
    example: '0917 123 4567',
  },
];

/** Default country (UAE) */
export const DEFAULT_COUNTRY_CODE = 'AE';

/**
 * Get country config by country code.
 */
export function getCountryConfig(countryCode: string): CountryPhoneConfig | undefined {
  return COUNTRY_PHONE_CONFIGS.find((c) => c.code === countryCode);
}

/**
 * Get country config by dial code.
 */
export function getCountryByDialCode(dialCode: string): CountryPhoneConfig | undefined {
  return COUNTRY_PHONE_CONFIGS.find((c) => c.dialCode === dialCode);
}

/**
 * Normalizes a phone number by:
 * - Converting Arabic numerals to English
 * - Removing spaces, dashes, parentheses, dots
 * - Keeping only digits and leading +
 *
 * @example
 * normalizePhone('٠٥٠١٢٣٤٥٦٧') // '0501234567'
 * normalizePhone('+971 50 123 4567') // '+971501234567'
 * normalizePhone('050-123-4567') // '0501234567'
 */
export function normalizePhone(phone: string | null | undefined): string {
  if (!phone) return '';

  let normalized = phone;

  // Convert Arabic numerals to English
  for (const [arabic, english] of Object.entries(ARABIC_TO_ENGLISH)) {
    normalized = normalized.replace(new RegExp(arabic, 'g'), english);
  }

  // Remove spaces, dashes, parentheses, dots
  normalized = normalized.replace(/[\s\-\(\)\.]/g, '');

  // Keep only digits and +
  normalized = normalized.replace(/[^\d+]/g, '');

  // Remove + if not at the start
  if (normalized.includes('+') && !normalized.startsWith('+')) {
    normalized = normalized.replace(/\+/g, '');
  }

  return normalized;
}

/**
 * Converts a phone number to E.164 format for canonical storage.
 * This is the format used for database storage and uniqueness checks.
 *
 * Handles all input formats:
 * - Local with leading 0: '0501234567' -> '+971501234567'
 * - Local without leading 0: '501234567' -> '+971501234567'
 * - International without +: '971501234567' -> '+971501234567'
 * - International with +: '+971501234567' -> '+971501234567'
 *
 * @param phone - The phone number to convert (any format)
 * @param countryCode - ISO country code (e.g., 'AE', 'SA'). Defaults to 'AE'.
 * @returns E.164 formatted phone (e.g., '+971501234567') or empty string if invalid
 *
 * @example
 * toE164('0501234567', 'AE')     // '+971501234567'
 * toE164('501234567', 'AE')      // '+971501234567'
 * toE164('971501234567', 'AE')   // '+971501234567'
 * toE164('+971501234567', 'AE')  // '+971501234567'
 * toE164('052 427 9777', 'AE')   // '+971524279777'
 */
export function toE164(phone: string | null | undefined, countryCode: string = DEFAULT_COUNTRY_CODE): string {
  if (!phone) return '';

  const config = getCountryConfig(countryCode);
  if (!config) return '';

  // First normalize: convert Arabic numerals, remove formatting
  let digits = normalizePhone(phone);

  // Remove leading + for processing
  const hadPlus = digits.startsWith('+');
  if (hadPlus) {
    digits = digits.slice(1);
  }

  // Case 1: Already has the country dial code (with or without +)
  // e.g., '971501234567' or '97152xxxxxxx'
  if (digits.startsWith(config.dialCode)) {
    const localPart = digits.slice(config.dialCode.length);
    // Validate the local part has correct number of digits
    if (localPart.length === config.localDigits) {
      return `+${config.dialCode}${localPart}`;
    }
    // Could be local number that coincidentally starts with dial code digits
    // Fall through to check as local number
  }

  // Case 2: Local number with leading 0 (e.g., '0501234567')
  if (digits.startsWith('0') && config.hasLeadingZero) {
    const withoutZero = digits.slice(1);
    if (withoutZero.length === config.localDigits) {
      return `+${config.dialCode}${withoutZero}`;
    }
  }

  // Case 3: Local number without leading 0 (e.g., '501234567')
  if (digits.length === config.localDigits) {
    return `+${config.dialCode}${digits}`;
  }

  // Case 4: Number already has dial code (validated length)
  if (digits.startsWith(config.dialCode)) {
    const localPart = digits.slice(config.dialCode.length);
    // Remove leading 0 if present in the local part after dial code
    const cleanLocal = localPart.startsWith('0') && config.hasLeadingZero
      ? localPart.slice(1)
      : localPart;
    if (cleanLocal.length === config.localDigits) {
      return `+${config.dialCode}${cleanLocal}`;
    }
  }

  // If input already had + and starts with a known dial code, try to validate
  if (hadPlus) {
    // Check if it matches any known country
    for (const countryConfig of COUNTRY_PHONE_CONFIGS) {
      if (digits.startsWith(countryConfig.dialCode)) {
        const localPart = digits.slice(countryConfig.dialCode.length);
        if (localPart.length === countryConfig.localDigits) {
          return `+${countryConfig.dialCode}${localPart}`;
        }
      }
    }
  }

  // Cannot convert - return empty to indicate invalid
  return '';
}

/**
 * Validates if a phone number can be converted to E.164 format.
 *
 * @param phone - The phone number to validate
 * @param countryCode - ISO country code (e.g., 'AE', 'SA')
 * @returns true if the phone can be normalized to E.164
 */
export function isValidPhoneForCountry(phone: string | null | undefined, countryCode: string = DEFAULT_COUNTRY_CODE): boolean {
  return toE164(phone, countryCode) !== '';
}

/**
 * Detects the country from a phone number if it has an international prefix.
 * Useful for auto-detecting country when user pastes a full international number.
 *
 * @param phone - The phone number (may or may not have country code)
 * @returns Country config if detected, undefined otherwise
 */
export function detectCountryFromPhone(phone: string | null | undefined): CountryPhoneConfig | undefined {
  if (!phone) return undefined;

  const normalized = normalizePhone(phone);
  const digits = normalized.startsWith('+') ? normalized.slice(1) : normalized;

  // Check each country's dial code (longest first to handle overlapping codes)
  const sortedConfigs = [...COUNTRY_PHONE_CONFIGS].sort(
    (a, b) => b.dialCode.length - a.dialCode.length
  );

  for (const config of sortedConfigs) {
    if (digits.startsWith(config.dialCode)) {
      const localPart = digits.slice(config.dialCode.length);
      // Verify the local part matches expected length
      if (localPart.length === config.localDigits) {
        return config;
      }
    }
  }

  return undefined;
}

/**
 * Extracts just the local part of a phone number (without country code).
 * Useful for displaying numbers in local format.
 *
 * @param e164Phone - Phone in E.164 format (e.g., '+971501234567')
 * @param countryCode - Expected country code
 * @returns Local number (e.g., '501234567') or original if not matching
 */
export function getLocalNumber(e164Phone: string | null | undefined, countryCode: string = DEFAULT_COUNTRY_CODE): string {
  if (!e164Phone) return '';

  const config = getCountryConfig(countryCode);
  if (!config) return e164Phone;

  const normalized = normalizePhone(e164Phone);
  const prefix = `+${config.dialCode}`;

  if (normalized.startsWith(prefix)) {
    return normalized.slice(prefix.length);
  }

  return normalized;
}

/**
 * Checks if the input looks like a complete phone number.
 * Handles multiple formats:
 * - 9 digits: Local without leading 0 (e.g., 501234567)
 * - 10 digits: Local with leading 0 (e.g., 0501234567)
 * - 12 digits: International with country code (e.g., 971501234567)
 * - 12+ digits with +: E.164 format (e.g., +971501234567)
 *
 * Smart detection - waits for complete number before triggering:
 * - Numbers starting with 0: waits for 10 digits
 * - Numbers starting with country code: waits for full international length
 * - Other local numbers: waits for 9 digits
 *
 * @example
 * looksLikePhoneNumber('501234567') // true (9 digits - UAE without leading 0)
 * looksLikePhoneNumber('0501234567') // true (10 digits - UAE with leading 0)
 * looksLikePhoneNumber('٠٥٠١٢٣٤٥٦٧') // true (10 Arabic digits)
 * looksLikePhoneNumber('+971501234567') // true (12 digits with +)
 * looksLikePhoneNumber('971501234567') // true (12 digits - international)
 * looksLikePhoneNumber('9715012345') // false (incomplete - starts with country code)
 * looksLikePhoneNumber('05267118') // false (incomplete - starts with 0, needs 10 digits)
 * looksLikePhoneNumber('John') // false
 */
export function looksLikePhoneNumber(input: string | null | undefined): boolean {
  if (!input) return false;

  const normalized = normalizePhone(input);

  // Remove leading + for digit analysis
  const digits = normalized.startsWith('+') ? normalized.slice(1) : normalized;

  // Must be only digits (after removing +)
  if (!/^\d+$/.test(digits)) return false;

  // Check if it starts with a known country code
  // If so, require the full international format (dial code + local digits)
  for (const config of COUNTRY_PHONE_CONFIGS) {
    if (digits.startsWith(config.dialCode)) {
      // For numbers starting with country code, require full length
      // e.g., 971 (3 digits) + 9 local digits = 12 digits minimum
      const expectedLength = config.dialCode.length + config.localDigits;
      if (digits.length >= expectedLength && digits.length <= expectedLength + 1) {
        return true;
      }
      // If it starts with a country code but isn't complete, don't trigger yet
      if (digits.length < expectedLength) {
        return false;
      }
    }
  }

  // For local numbers starting with 0 (UAE, Saudi, Egypt, etc.):
  // Require exactly 10 digits (0 + 9 local digits)
  if (digits.startsWith('0')) {
    return digits.length === 10;
  }

  // For local numbers NOT starting with 0 (e.g., 501234567):
  // Require exactly 9 digits (UAE/Saudi without leading 0)
  return digits.length === 9;
}

/**
 * Formats a phone number for display.
 * Supports UAE format (+971 XX XXX XXXX).
 *
 * @example
 * formatPhoneForDisplay('+971501234567') // '+971 50 123 4567'
 * formatPhoneForDisplay('0501234567') // '0501234567'
 */
export function formatPhoneForDisplay(phone: string | null | undefined): string {
  if (!phone) return '';

  const normalized = normalizePhone(phone);

  // UAE format: +971 XX XXX XXXX
  if (normalized.startsWith('+971') && normalized.length === 12) {
    return `+971 ${normalized.slice(4, 6)} ${normalized.slice(6, 9)} ${normalized.slice(9)}`;
  }

  // UAE local format: 0XX XXX XXXX
  if (normalized.startsWith('0') && normalized.length === 10) {
    return `${normalized.slice(0, 3)} ${normalized.slice(3, 6)} ${normalized.slice(6)}`;
  }

  // Return as-is for other formats
  return normalized;
}

/**
 * Extracts digits from a phone number for partial search.
 * Useful for searching by last N digits.
 *
 * @example
 * extractDigits('+971501234567') // '971501234567'
 * extractDigits('050-123-4567') // '0501234567'
 */
export function extractDigits(phone: string | null | undefined): string {
  if (!phone) return '';

  const normalized = normalizePhone(phone);
  return normalized.replace(/\D/g, '');
}
